import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  Cell,
  LabelList,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { IndianRupee, TrendingUp, Wallet, Clock, Users } from 'lucide-react';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import {
  PageBody,
  TileRow,
  FilterBar,
  SegmentedControl,
  Tile,
  Field,
  Th,
  Td
} from '../../../shared/components/ui/primitives';
import {
  getLabourSpend,
  getLabourSpendSummary
} from '../../services/api/implementation/impl';

/**
 * What labour has cost, and how much of it is still owed.
 *
 * ACCRUED AND PAID ARE NEVER ADDED TOGETHER, here or on the server. Accrued is
 * what the work was worth; paid is what has left the account. The gap between
 * them is the outstanding liability, and it is the number a site manager most
 * often actually wants — so it is shown as its own column rather than left for
 * somebody to subtract in their head.
 */

const GROUPS = [
  { key: 'project', label: 'By site' },
  { key: 'trade', label: 'By trade' },
  { key: 'worker', label: 'By workman' },
  { key: 'contractor', label: 'By contractor' },
  { key: 'day', label: 'By day' },
  { key: 'month', label: 'By month' }
];

/**
 * What to plot.
 *
 * The chart only ever showed rupees, so there was no way to see whether a
 * site's spend rose because it worked more hours or because it paid more for
 * them. Money, hours and man-days answer three different questions off the
 * same rows.
 */
const METRICS = [
  {
    key: 'money',
    label: 'Money',
    bars: [
      { dataKey: 'accrued', name: 'Accrued', fill: '#0f766e' },
      { dataKey: 'paid', name: 'Paid', fill: '#94a3b8' }
    ],
    format: (v) => formatMoney(v),
    axis: (v) => compactMoney(v)
  },
  {
    key: 'hours',
    label: 'Hours',
    bars: [{ dataKey: 'hours', name: 'Hours worked', fill: '#0369a1' }],
    format: (v) => `${Number(v || 0).toLocaleString('en-IN')} h`,
    axis: (v) => `${Math.round(Number(v || 0))}`
  },
  {
    key: 'mandays',
    label: 'Days',
    bars: [{ dataKey: 'mandays', name: 'Man-days', fill: '#b45309' }],
    format: (v) => `${Number(v || 0).toLocaleString('en-IN')} days`,
    axis: (v) => `${Math.round(Number(v || 0))}`
  }
];

/**
 * Clicking a bar asks "what is inside this?".
 *
 * Only three groupings have an inside worth opening — a site, a contractor and
 * a trade are all made of workmen. A day or a month is made of workmen too, but
 * people read those as a trend rather than a container, so a click there would
 * throw away the shape they came to see.
 *
 * The filter names are the ones the spend endpoint already accepts.
 */
const DRILLABLE = {
  project: { param: 'project_id', into: 'worker' },
  contractor: { param: 'sp_id', into: 'worker' },
  trade: { param: 'trade', into: 'worker' }
};

/**
 * What the bar is actually worth.
 *
 * The old tooltip repeated the one series being plotted, which a reader can
 * already see from the axis. The question people bring to this screen is "why
 * is that one big" — so it shows all of it: what was earned, what has been
 * paid, what is still owed, and the hours and man-days behind the money.
 *
 * Accrued and paid are never added together. They answer different questions.
 */
const SpendTooltip = ({ active, payload, drillable }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const line = (label, value) => (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium tabular-nums text-gray-900">{value}</span>
    </div>
  );
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-sm">
      <div className="mb-2 max-w-[220px] font-medium text-gray-900">{row.label}</div>
      <div className="grid gap-1">
        {line('Accrued', formatMoney(row.accrued))}
        {row.overtime ? line('of which overtime', formatMoney(row.overtime)) : null}
        {line('Paid', formatMoney(row.paid))}
        {line('Outstanding', formatMoney(row.outstanding))}
        <div className="my-1 border-t border-gray-100" />
        {line('Hours', `${Number(row.hours || 0).toLocaleString('en-IN')} h`)}
        {line('Man-days', Number(row.mandays || 0).toLocaleString('en-IN'))}
        {line('Workmen', Number(row.workers || 0).toLocaleString('en-IN'))}
      </div>
      {drillable ? (
        <div className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-brand-700">
          Click to see the workmen inside this
        </div>
      ) : null}
    </div>
  );
};

/** A day or a month reads left to right in time, not biggest first. */
const CHRONOLOGICAL = new Set(['day', 'month']);

const LabourSpend = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const [groupBy, setGroupBy] = useState('project');
  const openDrill = (row) => {
    const spec = DRILLABLE[groupBy];
    if (!spec || !row || row.key == null) return;
    setDrill({ ...spec, key: row.key, label: row.label, from: groupBy });
  };
  const [metricKey, setMetricKey] = useState('money');
  const [range, setRange] = useState({ from: '', to: '' });

  /**
   * What we have opened, if anything. Holds the row that was clicked so the
   * chip can name it — an id alone would read as "Filtered: 140".
   */
  const [drill, setDrill] = useState(null);

  const params = useMemo(
    () => ({
      group_by: drill ? drill.into : groupBy,
      ...(range.from ? { from: range.from } : {}),
      ...(range.to ? { to: range.to } : {}),
      ...(drill ? { [drill.param]: drill.key } : {})
    }),
    [groupBy, range, drill]
  );

  const summaryParams = useMemo(
    () => ({ ...range, ...(drill ? { [drill.param]: drill.key } : {}) }),
    [range, drill]
  );
  const summary = useQuery({
    queryKey: ['labourSpendSummary', summaryParams],
    queryFn: () => getLabourSpendSummary(summaryParams)
  });

  const spend = useQuery({
    queryKey: ['labourSpend', params],
    queryFn: () => getLabourSpend(params)
  });

  const rows = spend.data?.data ?? [];
  const meta = spend.data?.meta ?? {};
  const totals = summary.data ?? {};

  // Charting every row of a long-tailed list produces an unreadable axis. The
  // table below carries the rest, so nothing is hidden — only the chart is
  // trimmed, and it says so.
  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0];

  // Time reads left to right. Everything else is a ranking, so the biggest
  // bar goes first — which is the order the server already returns.
  const ordered = CHRONOLOGICAL.has(groupBy)
    ? [...rows].sort((a, b) => String(a.label).localeCompare(String(b.label)))
    : rows;

  // A day-by-day range is a time series, not a top-twelve: trimming it would
  // silently drop the end of the period somebody chose.
  const charted = CHRONOLOGICAL.has(groupBy) ? ordered : ordered.slice(0, 12);

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Labour Spend' }} />

      <PageBody>
        <TileRow>
          <Tile variant="card"
            label="Accrued"
            hint="What the work was worth"
            value={formatMoney(totals.accrued)}
            icon={<TrendingUp size={16} />}
          />
          <Tile variant="card"
            label="Paid"
            hint="What has left the account"
            value={formatMoney(totals.paid)}
            icon={<IndianRupee size={16} />}
          />
          <Tile variant="card"
            label="Outstanding"
            hint="Still owed"
            value={formatMoney(totals.outstanding)}
            icon={<Wallet size={16} />}
            tone="warning"
          />
          <Tile variant="card"
            label="Overtime"
            hint="Included in accrued"
            value={formatMoney(totals.overtime)}
            icon={<Clock size={16} />}
          />
          <Tile variant="card"
            label="Workmen"
            hint="With entries in range"
            value={totals.workers ?? 0}
            icon={<Users size={16} />}
          />
          <Tile variant="card"
            label="Hours"
            hint="Worked in range"
            value={`${Number(meta.total_hours ?? 0).toLocaleString('en-IN')} h`}
            icon={<Clock size={16} />}
          />
          <Tile variant="card"
            label="Man-days"
            hint="One workman, one date"
            value={Number(meta.total_mandays ?? 0).toLocaleString('en-IN')}
            icon={<Users size={16} />}
          />
        </TileRow>

        <FilterBar
          end={
            <>
              <Field label="From" size="caption">
                <input
                  type="date"
                  value={range.from}
                  onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                  className="rounded-control border border-hairline-strong bg-surface-muted px-2 py-1 text-xs text-gray-700 outline-none focus:border-brand-700"
                />
              </Field>
              <Field label="To" size="caption">
                <input
                  type="date"
                  value={range.to}
                  onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                  className="rounded-control border border-hairline-strong bg-surface-muted px-2 py-1 text-xs text-gray-700 outline-none focus:border-brand-700"
                />
              </Field>
              {(range.from || range.to) && (
                <button
                  type="button"
                  onClick={() => setRange({ from: '', to: '' })}
                  className="mb-1.5 text-xs font-semibold text-brand-700 hover:underline">
                  Clear
                </button>
              )}
            </>
          }>
          <SegmentedControl
            ariaLabel="Group by"
            value={groupBy}
            onChange={(key) => {
              setGroupBy(key);
              // Regrouping invalidates whatever was drilled into: the open
              // scope names a row that need not exist under the new grouping.
              setDrill(null);
            }}
            options={GROUPS.map((g) => ({ value: g.key, label: g.label }))}
          />

          {/* What to plot. Separate from what to group by: the same rows
              answer three different questions depending on which you read.
              Neutral tone so the two rows do not compete for the same green. */}
          <SegmentedControl
            ariaLabel="Metric"
            tone="neutral"
            value={metricKey}
            onChange={setMetricKey}
            options={METRICS.map((m) => ({ value: m.key, label: m.label }))}
          />
        </FilterBar>

        {spend.isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center text-sm text-gray-500">
            No labour spend recorded in this range.
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-baseline justify-between">
                {drill ? (
                  <button
                    type="button"
                    onClick={() => setDrill(null)}
                    className="mb-2 inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-800 hover:bg-brand-100"
                  >
                    Inside {drill.label}
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">Show all again</span>
                  </button>
                ) : null}
                <h3 className="text-sm font-medium text-gray-800">
                  {GROUPS.find((g) => g.key === groupBy)?.label}
                </h3>
                {rows.length > charted.length && (
                  <span className="text-xs text-gray-500">
                    Top {charted.length} shown — all {rows.length} are in the table
                  </span>
                )}
              </div>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charted} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f2" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={metric.axis}
                      width={64}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(15, 118, 110, 0.06)' }}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      content={<SpendTooltip drillable={Boolean(DRILLABLE[groupBy])} />}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {metric.bars.map((bar, barIndex) => (
                      <Bar
                        key={bar.dataKey}
                        dataKey={bar.dataKey}
                        name={bar.name}
                        fill={bar.fill}
                        radius={[3, 3, 0, 0]}
                        cursor={DRILLABLE[groupBy] ? 'pointer' : undefined}
                        onClick={(payload) => openDrill(payload?.payload ?? payload)}
                      >
                        {/* The number itself, so a figure can be read without hovering.
                            Only on the last series, or two labels sit on one another, and
                            only when the bars are wide enough to hold one. */}
                        {barIndex === metric.bars.length - 1 && charted.length <= 12 ? (
                          <LabelList
                            dataKey={bar.dataKey}
                            position="top"
                            formatter={(v) => metric.axis(v)}
                            style={{ fontSize: 10, fill: '#475569' }}
                          />
                        ) : null}
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <Th>{GROUPS.find((g) => g.key === groupBy)?.label.replace('By ', '')}</Th>
                      <Th align="right">Workmen</Th>
                      <Th align="right">Days</Th>
                      <Th align="right">Hours</Th>
                      <Th align="right">Accrued</Th>
                      <Th align="right">Overtime</Th>
                      <Th align="right">Paid</Th>
                      <Th align="right">Outstanding</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={`${row.key}-${row.label}`}
                        onClick={() => openDrill(row)}
                        className={`border-t border-gray-100 ${
                          DRILLABLE[groupBy] ? 'cursor-pointer hover:bg-brand-50/60' : ''
                        }`}
                      >
                        <Td className="font-medium text-gray-800">{row.label}</Td>
                        <Td align="right" className="text-gray-500">{row.workers}</Td>
                        <Td align="right" className="tabular-nums text-gray-500">
                          {Number(row.mandays ?? 0).toLocaleString('en-IN')}
                        </Td>
                        <Td align="right" className="tabular-nums text-gray-500">
                          {Number(row.hours ?? 0).toLocaleString('en-IN')}
                        </Td>
                        <Td align="right" numeric>{formatMoney(row.accrued)}</Td>
                        <Td align="right" className="tabular-nums text-gray-500">
                          {formatMoney(row.overtime)}
                        </Td>
                        <Td align="right" className="tabular-nums text-gray-500">
                          {formatMoney(row.paid)}
                        </Td>
                        <Td
                          className={`text-right tabular-nums font-medium ${
                            Number(row.outstanding) > 0 ? 'text-warning-fg' : 'text-gray-700'
                          }`}
                        >
                          {formatMoney(row.outstanding)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-medium">
                    <tr>
                      <Td>Total</Td>
                      <Td />
                      <Td align="right" numeric>
                        {Number(meta.total_mandays ?? 0).toLocaleString('en-IN')}
                      </Td>
                      <Td align="right" numeric>
                        {Number(meta.total_hours ?? 0).toLocaleString('en-IN')}
                      </Td>
                      <Td align="right" numeric>
                        {formatMoney(meta.total_accrued)}
                      </Td>
                      <Td align="right" numeric>
                        {formatMoney(meta.total_overtime)}
                      </Td>
                      <Td align="right" numeric>
                        {formatMoney(meta.total_paid)}
                      </Td>
                      <Td align="right" numeric>
                        {formatMoney(meta.total_outstanding)}
                      </Td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </PageBody>
    </Layout>
  );
};


function formatMoney(value) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '₹0';
  return `₹${number.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

/** Axis labels: ₹1.2L reads at a glance where ₹120,000 does not. */
function compactMoney(value) {
  const n = Number(value ?? 0);
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000) return `₹${Math.round(n / 1000)}k`;
  return `₹${Math.round(n)}`;
}

export default LabourSpend;
